<template>
  <div
    :id="reservation.id"
    class="sm:h-12 flex flex-col sm:flex-row items-center flex-nowrap gap-y-2 sm:gap-2 text-n-0 font-body text-sm w-full px-2 py-4 sm:py-0 border-b border-n-600 cursor-pointer hover:bg-n-700"
    :class="{
      highlight: highlight,
      'opacity-30 cursor-none pointer-events-none': disabled
    }"
  >
    <div class="flex items-center gap-2 w-full sm:contents">
      <div class="w-12 flex items-center gap-1 shrink-0">
        {{ format(reservation.dateTime, 'HH:mm') }}
      </div>
      <div class="w-full sm:w-6/12">{{ reservation.name }} {{ reservation.surname }}</div>
      <div class="flex items-center justify-center shrink-0 min-w-12">
        <l-status
          v-if="reservation.status === 'no-show'"
          size="small"
          type="secondary"
          :show-icon="false"
          status="error"
        >
          {{ t('reservations.no-show') }}
        </l-status>
        <l-status
          v-else-if="isLate"
          size="small"
          type="secondary"
          :show-icon="false"
          status="pending"
        >
          {{ t('reservations.late') }}
        </l-status>
      </div>
      <div class="sm:w-16 w-6 flex items-center justify-center gap-1 shrink-0">
        <l-tooltip v-if="reservation.source" placement="auto" @click.stop>
          <div class="w-6 h-6 rounded-full overflow-hidden">
            <img
              v-if="reservation.source === 'CoverManager'"
              src="@/assets/bookings/CoverManager.png"
              class="w-full h-full"
            />
            <img
              v-if="reservation.source === 'TheFork'"
              src="@/assets/bookings/TheFork.png"
              class="w-full h-full"
            />
            <img
              v-if="reservation.source === 'GoogleBookings'"
              src="@/assets/bookings/GoogleBookings.png"
              class="w-full h-full"
            />
            <img
              v-if="reservation.source === 'LastBooking'"
              src="@/assets/bookings/Admin.png"
              class="w-full h-full"
            />
            <img
              v-if="reservation.source === 'admin' || reservation.source === 'pos'"
              src="@/assets/bookings/Pos.png"
              class="w-full h-full"
            />
            <img
              v-if="reservation.source === 'Restoo'"
              src="@/assets/bookings/Restoo.png"
              class="w-full h-full"
            />
          </div>
          <template #content>
            {{ reservation.source }}
          </template>
        </l-tooltip>
      </div>
    </div>
    <div class="flex items-center gap-2 w-full sm:contents">
      <div class="w-16 flex items-center justify-center gap-1 shrink-0">
        <l-tooltip v-if="reservation.customerComments" @click.stop>
          <l-icon v-if="reservation.customerComments" name="chat" />
          <template #content>
            {{ reservation.customerComments }}
          </template>
        </l-tooltip>
      </div>
      <div class="w-16 flex items-center gap-1 shrink-0">
        <l-icon name="people" />
        {{ reservation.diners }}
      </div>
      <div class="w-full sm:w-3/12 flex items-center gap-1 min-w-0">
        <l-icon name="table" />
        <div class="text-ellipsis overflow-hidden whitespace-nowrap capitalize">
          {{ tables }}
        </div>
      </div>
      <div class="w-full sm:w-3/12 flex items-center gap-1 min-w-0">
        <l-icon name="floorplan" />
        <div class="text-ellipsis overflow-hidden whitespace-nowrap">
          {{ floorplan }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { LIcon, LStatus, LTooltip } from '@last/core-ui/paprika'

import { useFloorplanStore } from '@/store/floorplan'
import type { Reservation } from '@/types'

const { t } = useI18n()

const props = defineProps<{
  reservation: Reservation
  time: Date
  highlight?: boolean
  disabled?: boolean
}>()

const floorplanStore = useFloorplanStore()
const { tables: allTables } = storeToRefs(floorplanStore)

const tables = computed(() => {
  const { tables, zone } = props.reservation
  let formattedTables: string[] = []
  if (tables && tables.length) {
    formattedTables = tables.map(table => allTables.value[table]?.name).filter(Boolean)
  }
  return (formattedTables.length > 0 && formattedTables.join(', ')) || zone || '-'
})

const floorplan = computed(() => {
  const { tables } = props.reservation
  if (tables && tables.length) {
    return floorplanStore.getTableFloorplan(tables[0])?.name || '-'
  }
  return '-'
})

const isLate = computed(() => {
  const { dateTime, tabId } = props.reservation
  return !tabId && dateTime && new Date(dateTime) < props.time
})
</script>

<style scoped>
.highlight {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    @apply bg-n-700;
  }
  50% {
    @apply bg-n-800;
  }
}
</style>
